// src/components/Layout.tsx - CORRIGIDO
import { Outlet, useLocation, Link, useNavigate } from "react-router";
import { useState, useEffect } from "react";
import {
  LayoutDashboard, ShoppingCart, ClipboardList, Package, Users, Truck,
  Warehouse, DollarSign, Wallet, BarChart3, Settings, Smartphone, Globe,
  Menu, LogOut, Receipt, ChefHat, CircleDot, Wifi, WifiOff, Sun, Moon,
  Monitor, Calculator, ShoppingBag, Store, Barcode, FileText
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useAuth } from "@/hooks/useAuth";
import { trpc } from "@/providers/trpc";
import { useTheme } from "@/providers/ThemeProvider";

// ─── ITENS DO MENU ───
const navItems = [
  { path: "/", label: "Dashboard", icon: LayoutDashboard },
  { path: "/pdv", label: "PDV / Vendas", icon: ShoppingCart },
  { path: "/comandas", label: "Comandas", icon: ClipboardList },
  { path: "/produtos", label: "Produtos", icon: Package },
  { path: "/barcode-print", label: "Etiquetas", icon: Barcode },
  { path: "/clientes", label: "Clientes", icon: Users },
  { path: "/fornecedores", label: "Fornecedores", icon: Truck },
  { path: "/estoque", label: "Estoque", icon: Warehouse },
  { path: "/caixa", label: "Caixa", icon: Wallet },
  { path: "/financeiro", label: "Financeiro", icon: DollarSign },
  { path: "/relatorios", label: "Relatórios", icon: BarChart3 },
  { path: "/garcom", label: "Comanda Mobile", icon: Smartphone },
  { path: "/entregador", label: "Entregador", icon: Globe },
  { path: "/pedidos-online", label: "Pedidos Online", icon: ShoppingBag },
  { path: "/cardapio-config", label: "Cardápio Digital", icon: Store },
  { path: "/configuracoes", label: "Configurações", icon: Settings },
  // ✅ ADICIONADO LOGS NO MENU
  { path: "/logs", label: "Logs", icon: FileText },
];

function SidebarContent({ onNavigate }: { onNavigate?: () => void }) {
  const location = useLocation();
  const { user, logout } = useAuth();
  const { resolvedTheme, setTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  const isOnModernPDV = location.pathname === "/pdv";
  const isOnSupermarketPDV = location.pathname === "/pdv-supermercado";
  const isOnPDV = isOnModernPDV || isOnSupermarketPDV;

  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b border-border">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center">
            <ChefHat className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="font-bold text-card-foreground text-sm">PDV EA Studio Dev</h1>
            <p className="text-xs text-muted-foreground">Edição Profissional</p>
          </div>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto py-2 px-2 space-y-1">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + "/");
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={onNavigate}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? "bg-orange-500/20 text-orange-500 border border-orange-500/30"
                  : "text-muted-foreground hover:bg-accent hover:text-card-foreground"
              }`}
            >
              <Icon className="w-5 h-5 shrink-0" />
              <span>{item.label}</span>
            </Link>
          );
        })}
      </nav>

      <div className="p-3 border-t border-border space-y-2">
        <button
          onClick={() => setTheme(isDark ? "light" : "dark")}
          className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:bg-accent hover:text-card-foreground transition-colors w-full"
        >
          {isDark ? <Sun className="w-5 h-5 shrink-0" /> : <Moon className="w-5 h-5 shrink-0" />}
          <span>{isDark ? "Modo Claro" : "Modo Escuro"}</span>
        </button>

        {isOnPDV && (
          <>
            {location.pathname === "/pdv" && (
              <Link
                to="/pdv-supermercado"
                onClick={onNavigate}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:bg-accent hover:text-card-foreground transition-colors w-full"
              >
                <Calculator className="w-5 h-5 shrink-0" />
                <span>Modo Tradicional</span>
              </Link>
            )}

            {location.pathname === "/pdv-supermercado" && (
              <Link
                to="/pdv"
                onClick={onNavigate}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:bg-accent hover:text-card-foreground transition-colors w-full"
              >
                <Monitor className="w-5 h-5 shrink-0" />
                <span>Modo Moderno</span>
              </Link>
            )}
          </>
        )}

        {user && (
          <div className="flex items-center gap-2 px-3 py-2">
            <div className="w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center text-white font-bold text-sm">
              {user.name?.charAt(0).toUpperCase() || "A"}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-card-foreground truncate">{user.name || "Usuário"}</p>
              <p className="text-xs text-muted-foreground truncate">{user.email || ""}</p>
            </div>
          </div>
        )}

        <Button
          variant="ghost"
          className="w-full justify-start text-muted-foreground hover:text-card-foreground hover:bg-accent"
          onClick={logout}
        >
          <LogOut className="w-4 h-4 mr-2" />
          Sair
        </Button>
      </div>
    </div>
  );
}

function OnlineStatus() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  return (
    <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full ${isOnline ? "bg-green-500/10" : "bg-red-500/10"}`}>
      {isOnline ? <Wifi className="w-3 h-3 text-green-500" /> : <WifiOff className="w-3 h-3 text-red-500" />}
      <span className={`text-[10px] font-medium ${isOnline ? "text-green-500" : "text-red-500"}`}>
        {isOnline ? "Online" : "Offline"}
      </span>
    </div>
  );
}

function CashRegisterStatus() {
  const { data: cashRegister } = trpc.financial.cashRegisterCurrent.useQuery();
  const isOpen = cashRegister?.status === "open";

  return (
    <Link
      to="/caixa"
      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-all cursor-pointer ${
        isOpen
          ? "bg-green-500/20 hover:bg-green-500/30 border border-green-500/30"
          : "bg-red-500/20 hover:bg-red-500/30 border border-red-500/30"
      }`}
    >
      <CircleDot className={`w-3 h-3 ${isOpen ? "text-green-500 animate-pulse" : "text-red-500"}`} />
      <div className="flex flex-col leading-tight">
        <span className={`text-xs font-bold ${isOpen ? "text-green-500" : "text-red-500"}`}>
          {isOpen ? "Caixa Aberto" : "Caixa Fechado"}
        </span>
        {isOpen && cashRegister?.openedBy && (
          <span className="text-[10px] text-muted-foreground">{cashRegister.openedBy}</span>
        )}
      </div>
    </Link>
  );
}

export default function Layout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && !user) {
      navigate("/login", { replace: true });
    }
  }, [isLoading, user, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const currentPage = navItems.find((n) => location.pathname === n.path || location.pathname.startsWith(n.path + "/"));

  // PDV Supermercado não usa o layout padrão
  if (location.pathname === "/pdv-supermercado") {
    return <Outlet />;
  }

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar Desktop */}
      <aside className="hidden lg:block w-64 bg-card border-r border-border fixed h-screen z-30">
        <SidebarContent key={location.pathname} />
      </aside>

      {/* Sidebar Mobile */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetTrigger asChild>
          <Button variant="ghost" size="icon" className="lg:hidden fixed top-3 left-3 z-50 bg-card text-card-foreground hover:bg-accent">
            <Menu className="w-5 h-5" />
          </Button>
        </SheetTrigger>
        <SheetContent side="left" className="w-64 p-0 bg-card border-border">
          <SidebarContent key={location.pathname} onNavigate={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>

      {/* Conteúdo Principal */}
      <main className="flex-1 lg:ml-64 min-h-screen overflow-y-auto">
        {/* Header */}
        <header className="sticky top-0 z-20 bg-card/80 backdrop-blur-md border-b border-border px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3 lg:gap-0">
            <div className="lg:hidden w-10" />
            <div className="flex items-center gap-2">
              <Receipt className="w-5 h-5 text-orange-500" />
              <h2 className="text-lg font-semibold text-card-foreground">
                {currentPage?.label || "Dashboard"}
              </h2>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <OnlineStatus />
            <CashRegisterStatus />
            {user && (
              <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-muted rounded-full">
                <div className="w-6 h-6 rounded-full bg-orange-500 flex items-center justify-center text-white text-xs font-bold">
                  {user.name?.charAt(0).toUpperCase() || "A"}
                </div>
                <span className="text-xs text-muted-foreground">{user.name || "Usuário"}</span>
              </div>
            )}
          </div>
        </header>

        {/* Conteúdo da Página */}
        <div className="p-4 lg:p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}